<template>
  <div class="chat-service">
    <!-- 错误页面：客服不存在 -->
    <div v-if="agentNotFound" class="qd-error-container">
      <div class="qd-error-icon">⚠️</div>
      <div class="qd-error-title">链接无效</div>
      <div class="qd-error-message">该客服账号不存在或链接已失效</div>
    </div>

    <!-- 加载中 -->
    <div v-else-if="isVerifying" class="qd-loading-container">
      <div class="qd-loading-spinner"></div>
      <div class="qd-loading-text">正在连接客服...</div>
    </div>

    <!-- 正常聊天界面 -->
    <div v-else class="qd-chat-wrapper">
      <!-- 导航栏 -->
      <div class="nav-bar">
        <div class="back-btn" @click="goBack">
          <svg viewBox="0 0 24 24">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </div>
        <h1 class="nav-title">在线客服</h1>
        <div class="nav-placeholder"></div>
      </div>

      <!-- 警告横幅 -->
      <div v-if="showWarning" class="warning-banner">
        <div class="warning-content">
          <span class="warning-text">人工服务时间 9:00-22:00；千岛官方客服只在 APP 内处理问题，任何站外沟通、点击链接均为诈骗，请务必甄别！</span>
        </div>
        <div class="warning-close" @click="showWarning = false">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </div>
      </div>

      <!-- 聊天区域 -->
      <div class="chat-container" ref="chatContainer">
        <!-- 客服信息 -->
        <div class="service-info">
          <span>客服</span>
          <span class="robot-name">白菜</span>
          <span>为您服务</span>
        </div>

        <!-- 时间戳 -->
        <div class="timestamp">{{ currentDate }}</div>

        <!-- 固定的欢迎消息 -->
        <div class="message-list">
          <div class="message-item service">
            <div class="avatar service-avatar">
              <img :src="serviceAvatarUrl" alt="客服头像" />
            </div>
            <div class="message-content">
              <div class="message-bubble">
                <p>您好，很高兴为您服务。如果有任何问题或需要帮助，请随时告诉我！</p>
              </div>
            </div>
          </div>

          <!-- 动态消息列表 -->
          <template v-for="msg in chatMessages" :key="msg.id">
            <!-- 客服消息 (左边) -->
            <div v-if="msg.speakerType !== 1" class="message-item service">
              <div class="avatar service-avatar">
                <img :src="serviceAvatarUrl" alt="客服头像" />
              </div>
              <div class="message-content">
                <!-- 支付卡片 -->
                <div v-if="isPaymentCard(msg.content)" class="order-card">
                  <div class="order-header">
                    <span class="order-title">安全支付</span>
                    <span class="order-status">待支付</span>
                  </div>
                  <div class="order-info">
                    <div class="product-name">订单编号：{{ getPaymentOrderId(msg.content) }}</div>
                  </div>
                  <div class="faq-list">
                    <div class="faq-item" @click="openPaymentPage(msg.content)">
                      <span class="faq-dot"></span>
                      <span class="faq-text">前往支付</span>
                      <svg class="faq-arrow" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <!-- 订单卡片消息 -->
                <div v-else-if="isOrderMessage(msg.content)" class="qd-order-card-msg">
                  <div class="qd-order-card-inner">
                    <img class="qd-order-card-img" :src="parseOrderMessage(msg.content).image" :alt="parseOrderMessage(msg.content).title">
                    <div class="qd-order-card-body">
                      <div class="qd-order-card-detail">
                        <h4 class="qd-order-card-name">{{ parseOrderMessage(msg.content).title }}</h4>
                        <div class="qd-order-card-price-wrap">
                          <span class="qd-order-card-currency">¥</span>
                          <span class="qd-order-card-price">{{ parseOrderMessage(msg.content).price }}</span>
                          <span class="qd-order-card-count">×1</span>
                        </div>
                      </div>
                      <span class="qd-order-card-status">交易中 ›</span>
                    </div>
                  </div>
                </div>
                <!-- 链接卡片消息 -->
                <div v-else-if="isLinkCardMessage(msg.content)" class="link-card-msg">
                  <div class="link-card-inner">
                    <h4 class="link-card-title">{{ parseLinkCardMessage(msg.content).title }}</h4>
                    <div class="link-card-btn" @click.stop="openCardLink(parseLinkCardMessage(msg.content).link)">{{ parseLinkCardMessage(msg.content).btnText || '查看详情' }}</div>
                  </div>
                </div>
                <!-- 图片消息 -->
                <div v-else-if="isImageMessage(msg.content)" class="message-bubble">
                  <img :src="getImageUrl(msg.content)" class="qd-msg-image" referrerpolicy="no-referrer" @click="previewImage(msg.content)">
                </div>
                <!-- 普通消息 -->
                <div v-else class="message-bubble">
                  <p v-html="escapeHtml(msg.content)"></p>
                </div>
              </div>
            </div>

            <!-- 用户消息 (右边) -->
            <div v-else class="message-item user">
              <div class="avatar user-avatar">
                <img :src="userAvatarUrl" alt="用户头像" />
              </div>
              <div class="message-content">
                <!-- 支付卡片 -->
                <div v-if="isPaymentCard(msg.content)" class="user-order-card">
                  <div class="user-order-header">
                    <span class="user-order-title">安全支付</span>
                    <span class="user-order-status">待支付</span>
                  </div>
                  <div class="user-order-info">
                    <div class="user-product-detail">
                      <div class="user-product-name">订单编号：{{ getPaymentOrderId(msg.content) }}</div>
                    </div>
                  </div>
                </div>
                <!-- 订单卡片消息 (用户发送样式) -->
                <div v-else-if="isOrderMessage(msg.content)" class="user-order-card">
                  <div class="user-order-header">
                    <span class="user-order-title">咨询订单</span>
                    <span class="user-order-status">交易中</span>
                  </div>
                  <div class="user-order-info">
                    <div class="user-product-image">
                      <img v-if="parseOrderMessage(msg.content).image" :src="parseOrderMessage(msg.content).image" alt="商品图片" />
                      <svg v-else viewBox="0 0 48 48" fill="none">
                        <rect width="48" height="48" rx="4" fill="#E8E8E8"/>
                      </svg>
                    </div>
                    <div class="user-product-detail">
                      <div class="user-product-name">{{ parseOrderMessage(msg.content).title }}</div>
                      <div class="user-product-price-count">
                        <span class="user-price">共 ¥{{ parseOrderMessage(msg.content).price }}</span>
                        <span class="user-count">，1 件</span>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- 链接卡片消息 (用户发送样式) -->
                <div v-else-if="isLinkCardMessage(msg.content)" class="link-card-msg">
                  <div class="link-card-inner">
                    <h4 class="link-card-title">{{ parseLinkCardMessage(msg.content).title }}</h4>
                    <div class="link-card-btn" @click.stop="openCardLink(parseLinkCardMessage(msg.content).link)">{{ parseLinkCardMessage(msg.content).btnText || '查看详情' }}</div>
                  </div>
                </div>
                <!-- 图片消息 -->
                <div v-else-if="isImageMessage(msg.content)" class="message-bubble user-bubble">
                  <img :src="getImageUrl(msg.content)" class="qd-msg-image" referrerpolicy="no-referrer" @click="previewImage(msg.content)">
                </div>
                <!-- 普通消息 -->
                <div v-else class="message-bubble user-bubble">
                  <p>{{ msg.content }}</p>
                </div>
                <!-- 发送失败标记 -->
                <span v-if="msg.sendFailed" class="send-failed" @click="resendMessage(msg)">⚠</span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- 底部操作栏 -->
      <div class="bottom-bar">
        <div class="action-buttons">
          <button class="action-btn send-order" @click="showOrderSelector = true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
              <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
              <line x1="8" y1="11" x2="16" y2="11"></line>
              <line x1="8" y1="15" x2="16" y2="15"></line>
            </svg>
            <span>发送订单</span>
          </button>
          <button class="action-btn end-chat">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 14L4 9l5-5"></path>
              <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11"></path>
            </svg>
            <span>结束本次会话</span>
          </button>
        </div>
        <div class="input-row">
          <div class="input-box">
            <input v-model="inputContent" type="text" class="message-input" placeholder="请输入想咨询的问题" @keyup.enter="sendMessage" />
          </div>
          <div class="input-actions">
            <template v-if="!inputContent.trim()">
              <button class="input-btn file-btn" title="图片" @click="triggerImageUpload">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              </button>
              <button class="input-btn plus-btn" title="更多">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="16"></line>
                  <line x1="8" y1="12" x2="16" y2="12"></line>
                </svg>
              </button>
            </template>
            <button v-else class="send-message-btn" @click="sendMessage">发送</button>
          </div>
        </div>
        <input type="file" ref="imageInput" accept="image/*" style="display:none" @change="handleImageUpload">
      </div>

      <!-- 图片预览模态框 -->
      <div class="image-preview-modal" :class="{ active: imagePreviewVisible }" @click.self="closeImagePreview">
        <img class="image-preview-content" :src="previewImageUrl" alt="预览图片">
      </div>

      <!-- 订单选择弹窗 -->
      <transition name="slide-up">
        <div v-if="showOrderSelector" class="order-selector-overlay" @click.self="showOrderSelector = false">
          <div class="order-selector">
            <div class="selector-header">
              <h3>为更快解决您的问题，请选择咨询内容</h3>
              <div class="close-btn" @click="showOrderSelector = false">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </div>
            </div>
            <!-- 搜索框 -->
            <div class="search-box">
              <svg class="search-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
              </svg>
              <input v-model="searchKeyword" type="text" placeholder="订单号/商品名称/卖家昵称/运单号" />
            </div>
            <!-- 订单状态标签 -->
            <div class="order-tabs">
              <div v-for="tab in orderTabs" :key="tab.value" :class="['tab-item', { active: currentTab === tab.value }]" @click="currentTab = tab.value">
                {{ tab.label }}
              </div>
            </div>
            <!-- 订单列表 -->
            <div class="order-list">
              <div v-for="order in filteredOrders" :key="order.id" class="order-list-item">
                <div class="order-item-header">
                  <div class="seller-info">
                    <div v-for="(avatar, aIndex) in order.sellerAvatars" :key="aIndex" class="seller-avatar" :style="{ background: avatar.bg }">
                      {{ avatar.emoji }}
                    </div>
                  </div>
                  <span class="order-item-status">{{ order.status }}</span>
                </div>
                <div class="order-item-info">
                  <span>{{ order.type }} 下单时间 {{ order.orderTime }}</span>
                  <span class="order-price">¥{{ order.price }}</span>
                </div>
                <div class="order-product">
                  <div class="product-image">
                    <img v-if="order.productImage" :src="order.productImage" alt="商品图片" />
                    <svg v-else viewBox="0 0 72 72" fill="none">
                      <rect width="72" height="72" rx="12" fill="#F5F5F5"/>
                      <circle cx="36" cy="30" r="10" fill="#DDD"/>
                      <rect x="20" y="46" width="32" height="5" rx="2" fill="#DDD"/>
                    </svg>
                  </div>
                  <div class="product-info-box">
                    <div class="product-title">{{ order.productTitle }}</div>
                    <div class="product-count">共 {{ order.count }} 件</div>
                  </div>
                  <button class="send-order-btn" @click="sendOrder(order)">发送</button>
                </div>
              </div>
              <div v-if="filteredOrders.length === 0" class="no-more">没有更多了</div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { apiFetch, API_BASE_URL } from '@/config/api'
import { chatWebSocket } from '@/utils/websocket'
import serviceAvatarImg from '@/assets/qd-service-avatar.png'
import userAvatarImg from '@/assets/qd-user-avatar.png'

export default {
  name: 'QdChat',
  props: {
    agentId: { type: String, default: '' }
  },
  data() {
    return {
      inputContent: '',
      chatMessages: [],
      lastMessageId: 0,
      customerName: '',
      agentAccount: '',
      sessionId: '',
      pollingTimer: null,
      statusPollingInterval: null,
      readPositionInterval: null,
      isVerifying: true,
      agentNotFound: false,
      wsUnsubscribe: null,
      showWarning: true,
      currentDate: '',
      serviceAvatarUrl: serviceAvatarImg,
      userAvatarUrl: userAvatarImg,
      imagePreviewVisible: false,
      previewImageUrl: '',
      showOrderSelector: false,
      searchKeyword: '',
      currentTab: 'all',
      orderTabs: [
        { label: '全部', value: 'all' },
        { label: '待支付', value: 'pending' },
        { label: '待发货', value: 'shipped' },
        { label: '待收货', value: 'received' },
        { label: '退款/退货中', value: 'refund' }
      ],
      orders: [],
    }
  },
  computed: {
    filteredOrders() {
      let result = this.orders
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase()
        result = result.filter(order =>
          order.productTitle.toLowerCase().includes(keyword)
        )
      }
      return result
    }
  },
  mounted() {
    // 生成当前日期显示
    const now = new Date()
    this.currentDate = `${now.getMonth() + 1}月${now.getDate()}日 ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`

    this.initSession()
    // 按接口计数访问
    const _up = new URLSearchParams(window.location.search)
    const _arid = _up.get('arid'), _artype = _up.get('artype')
    if (_arid && _artype) {
      const _dedupKey = `_tv_${_artype}_${_arid}`
      if (!sessionStorage.getItem(_dedupKey)) {
        sessionStorage.setItem(_dedupKey, '1')
        fetch(`${API_BASE_URL}/api/chat/track-visit?linkId=${_arid}&linkType=${_artype}`, { method: 'POST' }).catch(() => {})
      }
    }
    // 注册访客
    const _self = this
    setTimeout(() => {
      if (_self.agentAccount && _self.customerName) {
        fetch(`${API_BASE_URL}/api/chat/register-visit`, { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({sessionKey: `${_self.customerName}_${_self.agentAccount}`, agent: _self.agentAccount, customerName: _self.customerName, platform: 'qd'}) }).catch(() => {})
      }
    }, 2000)
    // 监听页面可见性变化
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeUnmount() {
    this.stopPolling()
    this.setCustomerOffline()
    this.disconnectWebSocket()
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  methods: {
    getAgentAccount() {
      if (this.agentId) return this.agentId
      const urlParams = new URLSearchParams(window.location.search)
      const agentParam = urlParams.get('custuuid') || urlParams.get('agent') || urlParams.get('id')
      if (agentParam) return agentParam
      const pathMatch = window.location.pathname.match(/\/qd-chat\/([^/]+)/)
      if (pathMatch) return pathMatch[1]
      return 'admin'
    },
    generateCustomerName() {
      const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
      let result = 'qd_'
      for (let i = 0; i < 8; i++) result += chars.charAt(Math.floor(Math.random() * chars.length))
      return result
    },
    getOrCreateCustomerName() {
      const agentId = this.getAgentAccount()
      const storageKey = `qd_customer_name_${agentId}`
      let name = localStorage.getItem(storageKey)
      if (!name || !name.startsWith('qd_')) {
        name = this.generateCustomerName()
        localStorage.setItem(storageKey, name)
      }
      return name
    },
    async verifyAgent() {
      this.isVerifying = true
      let verified = false
      while (!verified) {
        try {
          const res = await apiFetch(`/api/chat/verify-agent?agentAccount=${encodeURIComponent(this.agentAccount)}`)
          this.agentNotFound = !(res.code === 0 && res.data?.exists)
          if (res.code === 0 && res.data?.agentAccount) this.agentAccount = res.data.agentAccount
          verified = true
          this.isVerifying = false
          return !this.agentNotFound
        } catch (e) {
          console.error('验证客服账号失败，2秒后重试...', e)
          await new Promise(resolve => setTimeout(resolve, 2000))
        }
      }
    },
    async initSession() {
      this.agentAccount = this.getAgentAccount()
      this.customerName = this.getOrCreateCustomerName()
      this.sessionId = `${this.customerName}_${this.agentAccount}`
      console.log('千岛客服初始化:', this.sessionId)
      if (!(await this.verifyAgent())) return
      await this.loadOrders()
      await this.loadInitialMessages()
      this.connectWebSocket()
      this.startPolling()
      this.updateOnlineStatus()
    },
    async connectWebSocket() {
      try {
        await chatWebSocket.connect(this.sessionId)
        this.wsUnsubscribe = chatWebSocket.onMessage((data) => {
          this.handleWebSocketMessage(data)
        })
        console.log('WebSocket 连接成功')
      } catch (e) {
        console.log('WebSocket 连接失败，使用轮询:', e)
      }
    },
    handleWebSocketMessage(data) {
      if (data.type === 'newMessage' && data.sessionKey === this.sessionId) {
        // 跳过自己发送的消息（客户端speakerType=1），已通过乐观更新添加
        if (data.speakerType === 1) {
          const tempIdx = this.chatMessages.findIndex(m =>
            m.content === data.content && m.id > 1000000000000
          )
          if (tempIdx !== -1) {
            this.chatMessages[tempIdx].id = data.id
            this.lastMessageId = Math.max(this.lastMessageId, data.id)
          }
          return
        }
        const existingIds = new Set(this.chatMessages.map(m => m.id))
        if (!existingIds.has(data.id)) {
          this.chatMessages.push({
            id: data.id,
            speakerType: data.speakerType,
            content: data.content,
            createdAt: data.createdAt
          })
          if (data.id > this.lastMessageId) {
            this.lastMessageId = data.id
          }
          this.scrollToBottom()
          if (data.speakerType === 2) {
            this.playSound()
            this.updateReadPosition(data.id)
          }
        }
      }
    },
    disconnectWebSocket() {
      if (this.wsUnsubscribe) {
        this.wsUnsubscribe()
        this.wsUnsubscribe = null
      }
    },
    async loadInitialMessages() {
      try {
        const res = await apiFetch(`/api/chat/messages?sessionId=${encodeURIComponent(this.sessionId)}&viewerType=customer`)
        if (res.code === 0 && res.data?.length > 0) {
          this.chatMessages = res.data
          this.lastMessageId = Math.max(...res.data.map(m => m.id))
          this.scrollToBottom()
          this.updateReadPosition(this.lastMessageId)
        }
      } catch (e) { console.error(e) }
    },
    async updateReadPosition(lastReadMessageId) {
      if (!lastReadMessageId) return
      try {
        await apiFetch('/api/chat/read-position', {
          method: 'POST',
          body: JSON.stringify({
            sessionId: this.sessionId,
            userType: 'customer',
            lastReadMessageId: lastReadMessageId
          })
        })
      } catch (e) { console.log('更新已读位置失败:', e) }
    },
    async checkNewMessages() {
      try {
        const res = await apiFetch(`/api/chat/poll?sessionId=${encodeURIComponent(this.sessionId)}&lastId=${this.lastMessageId}`)
        if (res.code === 0 && res.data?.length > 0) {
          const ids = new Set(this.chatMessages.map(m => m.id))
          const newMsgs = res.data.filter(m => !ids.has(m.id))
          if (newMsgs.length > 0) {
            this.chatMessages.push(...newMsgs)
            this.lastMessageId = Math.max(...res.data.map(m => m.id))
            this.scrollToBottom()
            if (newMsgs.some(m => m.speakerType === 2)) {
              this.playSound()
              this.updateReadPosition(this.lastMessageId)
            }
          }
        }
      } catch (e) { console.log(e) }
      this.updateOnlineStatus()
    },
    startPolling() {
      const scheduleMessagePoll = () => {
        const delay = chatWebSocket.isConnected() ? 15000 : 3000
        this.pollingTimer = setTimeout(() => {
          this.checkNewMessages().then(() => {
            if (this.pollingTimer !== null) scheduleMessagePoll()
          })
        }, delay)
      }
      scheduleMessagePoll()
      this.statusPollingInterval = setInterval(() => this.sendHeartbeat(), 4000)
      this.readPositionInterval = setInterval(() => {
        if (this.lastMessageId && document.visibilityState === 'visible') this.updateReadPosition(this.lastMessageId)
      }, 30000)
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearTimeout(this.pollingTimer)
        this.pollingTimer = null
      }
      if (this.statusPollingInterval) clearInterval(this.statusPollingInterval)
      if (this.readPositionInterval) clearInterval(this.readPositionInterval)
    },
    async sendHeartbeat() {
      try {
        await apiFetch('/api/chat/heartbeat', {
          method: 'POST',
          body: JSON.stringify({ username: this.customerName })
        })
      } catch (e) { /* ignore */ }
    },
    async updateOnlineStatus() {
      try {
        await apiFetch('/api/chat/online-status', {
          method: 'POST',
          body: JSON.stringify({ username: this.customerName, userType: 'customer', isOnline: true })
        })
      } catch (e) { console.error(e) }
    },
    setCustomerOffline() {
      const data = JSON.stringify({ username: this.customerName, userType: 'customer', isOnline: false })
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${API_BASE_URL}/api/chat/online-status`, new Blob([data], { type: 'application/json' }))
      }
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        if (!chatWebSocket.isConnected()) {
          this.connectWebSocket()
        }
        this.loadInitialMessages()
        this.checkNewMessages()
        this.startPolling()
      } else {
        this.stopPolling()
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatContainer
        if (container) container.scrollTop = container.scrollHeight
      })
    },
    // 消息工具方法
    isImageMessage(c) { return c && c.startsWith('[图片:') && c.endsWith(']') },
    getImageUrl(c) {
      if (!this.isImageMessage(c)) return ''
      const imagePath = c.slice(4, -1)
      if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath
      return `${API_BASE_URL}/api/chat/image/${imagePath}`
    },
    isPaymentCard(c) { return c && /#payid\d+#/.test(c) },
    getPaymentOrderId(c) { const m = c && c.match(/#payid(\d+)#/); return m ? m[1] : '' },
    openPaymentPage(c) { const orderId = this.getPaymentOrderId(c); if (orderId) window.open(`${window.location.origin}/payment?order=${orderId}`, '_blank') },
    previewImage(c) {
      const url = this.getImageUrl(c)
      if (url) {
        this.previewImageUrl = url
        this.imagePreviewVisible = true
      }
    },
    closeImagePreview() {
      this.imagePreviewVisible = false
      this.previewImageUrl = ''
    },
    escapeHtml(text) {
      if (!text) return ''
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    },
    playSound() {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)()
        const osc = ctx.createOscillator(), gain = ctx.createGain()
        osc.connect(gain); gain.connect(ctx.destination)
        osc.frequency.value = 800; osc.type = 'sine'
        gain.gain.setValueAtTime(0.3, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5)
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.5)
      } catch (e) { /* ignore */ }
    },
    goBack() {
      if (window.history.length > 1) {
        window.history.back()
      }
    },
    sendOrder(order) {
      // 和闲鱼一样用 [订单:标题:价格:图片URL] 格式
      const orderContent = `[订单:${order.title}:${order.price}:${order.image}]`
      this.showOrderSelector = false

      const tempId = Date.now()
      this.chatMessages.push({
        id: tempId,
        speakerType: 1,
        content: orderContent,
        createdAt: new Date().toISOString()
      })
      this.scrollToBottom()
      this.sendToBackend(orderContent, tempId)
    },
    // 判断是否是订单卡片消息
    isOrderMessage(content) {
      return content && content.startsWith('[订单:') && content.endsWith(']')
    },
    // 解析订单卡片消息
    parseOrderMessage(content) {
      if (!this.isOrderMessage(content)) return { title: '', price: '', image: '' }
      const inner = content.slice(4, -1) // 去掉 [订单: 和 ]
      const lastHttpIndex = inner.lastIndexOf(':http')
      if (lastHttpIndex === -1) {
        const lastColon = inner.lastIndexOf(':')
        return {
          title: inner.substring(0, lastColon),
          price: inner.substring(lastColon + 1),
          image: 'https://img.alicdn.com/imgextra/i4/O1CN01Z5paLz1O0zuCC7osS_!!6000000001644-0-tps-250-250.jpg'
        }
      }
      const imageUrl = inner.substring(lastHttpIndex + 1)
      const beforeImage = inner.substring(0, lastHttpIndex)
      const lastColon = beforeImage.lastIndexOf(':')
      return {
        title: beforeImage.substring(0, lastColon),
        price: beforeImage.substring(lastColon + 1),
        image: imageUrl
      }
    },
    // 判断是否是链接卡片消息
    isLinkCardMessage(content) {
      return content && content.startsWith('[卡片:') && content.endsWith(']')
    },
    parseLinkCardMessage(content) {
      if (!this.isLinkCardMessage(content)) return { title: '', btnText: '查看详情', link: '' }
      var inner = content.slice(4, -1)
      var lastHttpIndex = inner.lastIndexOf(':http')
      if (lastHttpIndex === -1) {
        var parts = inner.split(':')
        if (parts.length >= 3) {
          var link = parts.pop()
          var btnText = parts.pop()
          return { title: parts.join(':'), btnText: btnText, link: link }
        }
        return { title: inner, btnText: '查看详情', link: '' }
      }
      var link = inner.substring(lastHttpIndex + 1)
      var beforeLink = inner.substring(0, lastHttpIndex)
      var lastColon = beforeLink.lastIndexOf(':')
      if (lastColon === -1) {
        return { title: beforeLink, btnText: '查看详情', link: link }
      }
      return {
        title: beforeLink.substring(0, lastColon),
        btnText: beforeLink.substring(lastColon + 1) || '查看详情',
        link: link
      }
    },
    openCardLink(link) { if (link) window.open(link, '_blank') },
    // 加载订单数据
    async loadOrders() {
      const urlParams = new URLSearchParams(window.location.search)
      const orderId = urlParams.get('orderId') || urlParams.get('orderid')
      if (!orderId) {
        this.orders = []
        return
      }
      try {
        const response = await fetch(`${API_BASE_URL}/api/order/${orderId}`)
        const result = await response.json()
        if (result.code === 0 && result.data) {
          const order = result.data
          this.orders = [{
            id: order.id,
            sellerAvatars: [{ emoji: '🌿', bg: '#8BC34A' }],
            status: '交易中',
            type: '闲置',
            orderTime: order.createTime || '',
            price: order.price,
            productImage: order.imagePath ? `${API_BASE_URL}/api/order/image/${order.imagePath}` : '',
            productTitle: order.title,
            title: order.title,
            image: order.imagePath ? `${API_BASE_URL}/api/order/image/${order.imagePath}` : 'https://img.alicdn.com/imgextra/i4/O1CN01Z5paLz1O0zuCC7osS_!!6000000001644-0-tps-250-250.jpg',
            count: 1
          }]
        } else {
          this.orders = []
        }
      } catch (e) {
        console.error('加载订单失败:', e)
        this.orders = []
      }
    },
    async sendMessage() {
      const content = this.inputContent.trim()
      if (!content) return

      const tempId = Date.now()
      const tempMsg = {
        id: tempId,
        speakerType: 1,
        content: content,
        createdAt: new Date().toISOString()
      }
      this.chatMessages.push(tempMsg)
      this.inputContent = ''
      this.scrollToBottom()

      await this.sendToBackend(content, tempId)
    },
    async resendMessage(msg) {
      msg.sendFailed = false
      await this.sendToBackend(msg.content, msg.id)
    },
    async sendToBackend(content, tempId) {
      this.updateOnlineStatus()
      let success = false
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const res = await apiFetch('/api/chat/send', {
            method: 'POST',
            body: JSON.stringify({
              sessionId: this.sessionId,
              agentAccount: this.agentAccount,
              speakerType: 1,
              content,
              customerName: this.customerName,
              platform: 'qd',
              isRead: false
            })
          })
          if (res.code === 0 && res.data?.messageId) {
            if (tempId) {
              const msgIndex = this.chatMessages.findIndex(m => m.id === tempId)
              if (msgIndex !== -1) {
                this.chatMessages[msgIndex].id = res.data.messageId
              }
            }
            this.lastMessageId = Math.max(this.lastMessageId, res.data.messageId)
          }
          success = true
          break
        } catch (e) {
          console.error(`发送消息失败 (尝试 ${attempt + 1}/3):`, e)
          if (attempt < 2) await new Promise(r => setTimeout(r, 1000))
        }
      }
      if (success) {
        setTimeout(() => this.checkNewMessages(), 500)
      } else {
        // 标记发送失败
        const msgIndex = this.chatMessages.findIndex(m => m.id === tempId)
        if (msgIndex !== -1) {
          this.chatMessages[msgIndex].sendFailed = true
        }
      }
    },
    triggerImageUpload() {
      this.$refs.imageInput.click()
    },
    async handleImageUpload(e) {
      const file = e.target.files[0]
      if (!file) return

      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif', 'image/tiff']
      if (!allowedTypes.includes(file.type) && !file.name.match(/\.(heic|heif|tiff|tif)$/i)) {
        alert('只支持 JPG、PNG、GIF、WebP、HEIC、HEIF、TIFF 格式的图片')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('图片大小不能超过 5MB')
        return
      }

      const formData = new FormData()
      formData.append('file', file)

      try {
        const response = await fetch(`${API_BASE_URL}/api/chat/upload-image`, {
          method: 'POST',
          body: formData
        })
        const result = await response.json()
        if (result.code === 0 && result.data?.imagePath) {
          const imageContent = `[图片:${result.data.imagePath}]`
          const tempId = Date.now()
          this.chatMessages.push({
            id: tempId,
            speakerType: 1,
            content: imageContent,
            createdAt: new Date().toISOString()
          })
          this.scrollToBottom()
          await this.sendToBackend(imageContent, tempId)
        } else {
          alert(result.msg || '图片上传失败')
        }
      } catch (err) {
        console.error('上传图片失败:', err)
        alert('图片上传失败，请重试')
      }
      e.target.value = ''
    }
  }
}
</script>

<style scoped>
.chat-service {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  background: linear-gradient(180deg, #F2F0FE 0%, #FFFFFF 100%);
  height: 100vh;
  height: var(--app-height, 100dvh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 错误/加载状态 */
.qd-chat-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}
.qd-error-container, .qd-loading-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #F2F0FE 0%, #FFFFFF 100%);
}
.qd-error-icon { font-size: 64px; margin-bottom: 20px; }
.qd-error-title { font-size: 20px; font-weight: 600; color: #333; margin-bottom: 10px; }
.qd-error-message { font-size: 14px; color: #999; }
.qd-loading-spinner {
  width: 40px; height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #8B5CF6;
  border-radius: 50%;
  animation: qd-spin 1s linear infinite;
  margin-bottom: 16px;
}
@keyframes qd-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
.qd-loading-text { font-size: 14px; color: #666; }

/* 导航栏 */
.nav-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: transparent;
  flex-shrink: 0;
}
.back-btn {
  width: 24px;
  height: 24px;
  cursor: pointer;
}
.back-btn svg {
  width: 100%;
  height: 100%;
  stroke: #333;
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.nav-title {
  font-size: 17px;
  font-weight: 600;
  color: #333;
}
.nav-placeholder {
  width: 24px;
}

/* 警告横幅 */
.warning-banner {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  background: rgba(255, 248, 231, 0.85);
  flex-shrink: 0;
}
.warning-content {
  flex: 1;
  padding-right: 8px;
}
.warning-text {
  font-size: 12px;
  color: #FF8C00;
  line-height: 1.5;
}
.warning-close {
  width: 18px;
  height: 18px;
  min-width: 18px;
  cursor: pointer;
}
.warning-close svg {
  width: 100%;
  height: 100%;
  fill: #999;
}

/* 聊天容器 */
.chat-container {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  background: transparent;
}

/* 客服信息 */
.service-info {
  text-align: center;
  font-size: 13px;
  color: #999;
  margin-bottom: 12px;
}
.robot-name {
  color: #666;
  margin: 0 4px;
}

/* 时间戳 */
.timestamp {
  text-align: center;
  font-size: 12px;
  color: #999;
  margin-bottom: 16px;
}

/* 消息列表 */
.message-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 消息项 - 客服 */
.message-item.service {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.message-item.service .avatar {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  margin-top: 4px;
}
.message-item.service .avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.message-item.service .message-content {
  flex: 1;
  max-width: calc(100% - 55px);
}
.message-item.service .message-bubble {
  background-color: #fff;
  padding: 14px 16px;
  border-radius: 12px 12px 12px 2px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  display: inline-block;
}
.message-item.service .message-bubble p {
  font-size: 16px;
  color: #333;
  line-height: 1.6;
  margin: 0;
}

/* 消息项 - 用户 */
.message-item.user {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  flex-direction: row-reverse;
}
.message-item.user .avatar {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  margin-top: 4px;
}
.message-item.user .avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.message-item.user .message-content {
  display: flex;
  justify-content: flex-end;
  max-width: calc(100% - 55px);
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.message-item.user .message-bubble {
  background-color: #fff;
  padding: 14px 16px;
  border-radius: 12px 12px 2px 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  display: inline-block;
}
.message-item.user .message-bubble p {
  font-size: 16px;
  color: #333;
  line-height: 1.6;
  margin: 0;
}

/* 发送失败 */
.send-failed {
  color: #ff4d4f;
  font-size: 18px;
  cursor: pointer;
  order: -1;
}

/* 图片消息 */
.qd-msg-image {
  max-width: 200px;
  min-width: 100px;
  min-height: 100px;
  background-color: #f0f0f0;
  border-radius: 8px;
  cursor: pointer;
}

/* 订单卡片消息 */
.qd-order-card-msg {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  max-width: 280px;
}
.qd-order-card-inner {
  display: flex;
  padding: 12px;
  gap: 10px;
}
.qd-order-card-img {
  width: 72px;
  height: 72px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
  background: #f5f5f5;
}
.qd-order-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
}
.qd-order-card-detail {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.qd-order-card-name {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.qd-order-card-price-wrap {
  display: flex;
  align-items: baseline;
  gap: 2px;
}
.qd-order-card-currency {
  font-size: 12px;
  color: #FF6B6B;
  font-weight: 500;
}
.qd-order-card-price {
  font-size: 16px;
  color: #FF6B6B;
  font-weight: 600;
}
.qd-order-card-count {
  font-size: 12px;
  color: #999;
  margin-left: 4px;
}
.qd-order-card-status {
  font-size: 12px;
  color: #8B5CF6;
  align-self: flex-end;
}

/* 链接卡片消息样式 */
.link-card-msg {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  max-width: 260px;
}
.link-card-inner {
  padding: 0.75rem;
}
.link-card-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: #111;
  margin: 0 0 10px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
.link-card-btn {
  display: block;
  width: 100%;
  padding: 8px 0;
  text-align: center;
  background: #007aff;
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.2s;
}
.link-card-btn:active {
  opacity: 0.8;
}

/* 订单卡片 */
.order-card {
  background-color: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px 10px;
}
.order-title {
  font-size: 15px;
  color: #333;
  font-weight: 500;
}
.order-status {
  font-size: 13px;
  color: #FF6B6B;
}
.order-info {
  margin: 0 16px 12px;
  padding: 12px 14px;
  background-color: #F7F7F7;
  border-radius: 10px;
}
.product-name {
  font-size: 15px;
  color: #333;
  font-weight: 500;
}

/* FAQ列表 */
.faq-list { padding: 6px 0 10px; }
.faq-item { display: flex; align-items: center; padding: 10px 16px; cursor: pointer; }
.faq-item:active { background-color: #f9f9f9; }
.faq-dot { width: 6px; height: 6px; min-width: 6px; border-radius: 50%; background-color: #5B7BF7; margin-right: 10px; }
.faq-text { flex: 1; font-size: 14px; color: #5B7BF7; }
.faq-arrow { width: 20px; height: 20px; fill: #5B7BF7; flex-shrink: 0; }

/* 用户订单卡片 (原模板样式) */
.user-order-card {
  background-color: #fff;
  border-radius: 12px 12px 2px 12px;
  padding: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  min-width: 240px;
  max-width: 280px;
}
.user-order-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.user-order-title { font-size: 16px; font-weight: 500; color: #333; }
.user-order-status { font-size: 14px; color: #999; }
.user-order-info { display: flex; background-color: #F8F8F8; border-radius: 8px; padding: 10px; gap: 10px; }
.user-product-image { width: 48px; height: 48px; border-radius: 4px; overflow: hidden; flex-shrink: 0; }
.user-product-image img, .user-product-image svg { width: 100%; height: 100%; object-fit: cover; }
.user-product-detail { display: flex; flex-direction: column; justify-content: space-between; flex: 1; }
.user-product-name { font-size: 15px; color: #333; display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
.user-product-price-count { font-size: 14px; }
.user-price { color: #FF6B6B; }
.user-count { color: #999; }

/* 底部操作栏 */
.bottom-bar {
  background-color: transparent;
  padding: 12px 16px;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
  flex-shrink: 0;
}
.action-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}
.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  background-color: #fff;
  transition: all 0.2s;
}
.action-btn.send-order {
  border: 1px solid #8B5CF6;
  color: #8B5CF6;
}
.action-btn.send-order svg {
  width: 16px;
  height: 16px;
  stroke: #8B5CF6;
}
.action-btn.end-chat {
  border: 1px solid transparent;
  color: #333;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.action-btn.end-chat svg {
  width: 16px;
  height: 16px;
  stroke: #333;
}
.action-btn:active {
  opacity: 0.8;
}

/* 输入区域 */
.input-row { display: flex; align-items: center; gap: 12px; }
.input-box {
  flex: 1;
  background-color: #fff;
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
}
.message-input {
  width: 100%;
  border: none;
  background: transparent;
  font-size: 15px;
  color: #333;
  outline: none;
}
.message-input::placeholder { color: #B0B0B0; }
.input-actions { display: flex; gap: 12px; align-items: center; }
.input-btn {
  width: 28px; height: 28px;
  border: none; background: transparent;
  cursor: pointer; display: flex;
  align-items: center; justify-content: center;
  padding: 0;
}
.input-btn svg { width: 26px; height: 26px; stroke: #000; }
.send-message-btn {
  padding: 6px 16px;
  background-color: #8B5CF6;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;
}
.send-message-btn:active { background-color: #7C3AED; }

/* 图片预览模态框 */
.image-preview-modal {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: none; align-items: center; justify-content: center;
  z-index: 9999;
}
.image-preview-modal.active { display: flex; }
.image-preview-content { max-width: 90%; max-height: 90%; object-fit: contain; }

/* 订单选择弹窗 */
.order-selector-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex; align-items: flex-end; justify-content: center;
  z-index: 1000;
}
.order-selector {
  background: #fff; width: 100%; height: 85vh;
  border-radius: 16px 16px 0 0;
  display: flex; flex-direction: column; overflow: hidden;
}
.selector-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px;
}
.selector-header h3 { font-size: 16px; font-weight: 600; color: #333; margin: 0; }
.close-btn { width: 24px; height: 24px; cursor: pointer; }
.close-btn svg { width: 100%; height: 100%; fill: #999; }
.search-box {
  display: flex; align-items: center;
  margin: 0 16px 16px; padding: 8px 12px;
  background: #F5F5F5; border-radius: 20px;
}
.search-icon { width: 18px; height: 18px; fill: #999; margin-right: 8px; flex-shrink: 0; }
.search-box input {
  flex: 1; border: none; background: transparent;
  font-size: 14px; color: #333; outline: none;
}
.search-box input::placeholder { color: #999; }
.order-tabs {
  display: flex; gap: 12px; padding: 0 16px 12px;
  overflow-x: auto; scrollbar-width: none;
  border-bottom: 1px solid #f5f5f5;
}
.order-tabs::-webkit-scrollbar { display: none; }
.tab-item {
  padding: 6px 12px; background: #F5F5F5; border-radius: 4px;
  font-size: 13px; color: #666; white-space: nowrap; cursor: pointer;
  transition: all 0.2s;
}
.tab-item.active { background: #F4F0FF; color: #8B5CF6; font-weight: 500; }
.order-list { flex: 1; overflow-y: auto; padding: 0 16px; background: #FAFAFA; }
.order-list-item { padding: 16px; background: #fff; border-radius: 8px; margin-top: 12px; }
.order-item-header {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;
}
.seller-info { display: flex; align-items: center; gap: 4px; }
.seller-avatar {
  width: 20px; height: 20px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; font-size: 12px;
}
.order-item-status { font-size: 13px; color: #999; }
.order-item-info {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px; font-size: 12px; color: #999;
}
.order-price { font-size: 16px; color: #000; font-weight: 600; }
.order-product { display: flex; align-items: center; gap: 12px; }
.product-image {
  width: 64px; height: 64px; border-radius: 6px;
  overflow: hidden; background: #F5F5F5; flex-shrink: 0;
}
.product-image img { width: 100%; height: 100%; object-fit: cover; }
.product-image svg { width: 100%; height: 100%; }
.product-info-box {
  flex: 1; display: flex; flex-direction: column;
  justify-content: space-between; height: 64px; padding: 2px 0;
}
.product-title {
  font-size: 14px; color: #333; line-height: 1.4;
  display: -webkit-box; -webkit-line-clamp: 2;
  -webkit-box-orient: vertical; overflow: hidden;
}
.product-count { font-size: 12px; color: #999; }
.send-order-btn {
  padding: 6px 16px; background: #8B5CF6; color: #fff;
  border: none; border-radius: 4px; font-size: 13px;
  cursor: pointer; flex-shrink: 0;
}
.send-order-btn:active { background: #7C3AED; }
.no-more { text-align: center; padding: 24px; font-size: 13px; color: #999; }

/* 弹窗动画 */
.slide-up-enter-active, .slide-up-leave-active { transition: all 0.3s ease-out; }
.slide-up-enter-from, .slide-up-leave-to { opacity: 0; }
.slide-up-enter-from .order-selector, .slide-up-leave-to .order-selector { transform: translateY(100%); }
.slide-up-enter-to .order-selector, .slide-up-leave-from .order-selector { transform: translateY(0); }

/* 滚动条样式 */
.chat-container::-webkit-scrollbar { width: 4px; }
.chat-container::-webkit-scrollbar-track { background: transparent; }
.chat-container::-webkit-scrollbar-thumb { background: #ddd; border-radius: 2px; }

</style>
